import { buildLoginRedirect } from '@/lib/auth/loginRedirect';
import { resolveLogoutDestination } from '@/lib/auth/resolveLogoutDestination';
import { cancelProactiveRefresh } from '@/lib/auth/tokenRefresh';
import { clearAuthStorage, useAuthStore } from '@/stores/authStore';

const ACCESS_TOKEN_KEY = 'elixaa_access_token';

/**
 * Listen for `storage` events fired when ANOTHER tab mutates localStorage.
 * When the access token is removed elsewhere (logout / idle lockout / refresh
 * failure in another tab), wipe this tab's session and navigate to login so
 * we don't keep running with stale auth.
 *
 * Storage events do NOT fire in the tab that performed the mutation, so this
 * is a pure cross-tab signal.
 *
 * Scope caveat: this only does anything for "remember me" logins. Both login
 * forms default `rememberMe` to false, which persists tokens to
 * sessionStorage (authStore.ts storageFor), and sessionStorage is per-tab, so
 * no storage event ever crosses tabs on the default path. Signals that must
 * work for session-scoped logins go through BroadcastChannel instead. See
 * presenceSignal.ts.
 *
 * Returns a cleanup function. Call it on unmount or when stopping the sync.
 */
export function startCrossTabAuthSync(): () => void {
  const handler = (event: StorageEvent): void => {
    if (event.key !== ACCESS_TOKEN_KEY) return;
    // Only react to removals. A new token written by another tab is a
    // refresh, not a logout — let it through.
    if (event.newValue) return;
    // Don't redirect if this tab isn't authenticated either.
    if (!useAuthStore.getState().isAuthenticated) return;

    cancelProactiveRefresh();
    const { user } = useAuthStore.getState();
    const dest = resolveLogoutDestination(user);
    const target = buildLoginRedirect(dest);
    clearAuthStorage();
    window.location.replace(target);
  };

  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('storage', handler);
  };
}
